/**
 *
 * FBAppointmentAnswer
 *
 */

import React, { memo, useCallback, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslation } from "react-i18next";

// styles
import './style.scss';

// layout components
import ErrorBoundary from 'engine/ErrorBoundary';

// custom react-multi-date-picker
import { DateObject } from 'react-multi-date-picker';
import { timeFormat, dateFormat } from 'utils/moment/moment.constant';

// assets
import CustomIcons from 'components/CustomIcons';

// antd component
import { Form, Radio, Checkbox, Button } from 'antd';
// components
import HiddenField from 'components/Forms/Fields/HiddenField';
// inner communication components 
import {
  FBMobileWrapper,
} from 'mobile/components/Communications';
// inner communication components 
import { SwitchOnCancelAll } from './components';

// utils
import { useRefState } from 'utils/hooks';


function FBAppointmentAnswer({
  form,
  showHeader,
  showAdminAnswer,
  showWrapper,
  type,
  appointments,
  values,
  actionRequired,
  loading,
  adminAnswer,
  hideActions,
  // actions
  onSubmit,
  // default props
  className,
  ...rest
}) {
  const childClassNames = classNames('tcf-fb-appointment-group-preview', 'tcf-fb-group-answer', className);
  const { t, i18n } = useTranslation();

  const [previewAppointment] = Form.useForm();
  const mainForm = !!form && form || previewAppointment;
  const formName = type;

  const [answers, answersRef, setAnswers] = useRefState([]);
  const [isCancelAll, setIsCancelAll] = useState(false);
  const [formRequired, formRequiredRef, setFormRequired] = useRefState(true);

  // Refresh current value based on parent`s states
  useEffect(() => {
    if (!!values) {
      const changedVals = Object.values(values);
      const hasCancelation = changedVals?.find(el => el?.["field-type"] == "switchCancelAll" && !!el?.["switchCancelAll-answer"]);
      if (!!hasCancelation) {
        setIsCancelAll(true);
        setFormRequired(false);
      }
      setAnswers(values || []);
      mainForm.setFieldsValue(values || []);
    } else if (!!!values) {
      setAnswers({});
      mainForm.resetFields();
    }
  }, [values]);

  const formatDateTimeValue = useCallback((value, format) => {
    if (!!!value) {
      return '';
    }

    if (typeof value == 'object') {
      return !!value && value.format(format);
    } else {
      return !!value && new DateObject(value).format(format);
    }

  }, [])

  const prepearGroup = useCallback((fields, type) => {
    let gruopOptions = [];
    if (!!fields?.['field-input'] && type) {
      gruopOptions = fields?.['field-input']?.map((opt, indx) => {
        const timeFrom = formatDateTimeValue(opt?.[`time-from`], timeFormat);
        const timeTo = formatDateTimeValue(opt?.[`time-to`], timeFormat);
        const label = `${timeFrom} - ${timeTo}`;
        return timeFrom && timeTo && {
          label,
          value: `${opt?.[`time-from`]} - ${opt?.[`time-to`]}`,
          key: indx
        }
      }).filter(el => !!el && Object.keys(el)?.length > 0)
    }
    return gruopOptions;
  }, [appointments])

  // custom Validation for Section fileds
  const checkValidCallback = useCallback(
    async (rule, values, callback) => {
/*       const hasAnyAnswer = !!answers && answers?.length > 0 && answers?.some(val => val?.['checkbox-answer']?.length > 0 || val?.['radio-answer']?.length > 0);
 */
      const isValid = !formRequiredRef?.current;
      if (isValid) {
        return Promise.resolve(isValid);
      } else {
        return Promise.reject(
          new Error(t("inputs.mandatoryField"))
        );
      }
    },
    [answers, formRequired, formRequiredRef?.current, isCancelAll],
  );

  const generatePreviewByType = useCallback((row, name, isRequired) => {
    switch (row['field-type']) {
      case 'checkbox':
        const checkboxOptions = prepearGroup(row, 'checkbox')

        return checkboxOptions?.length > 0 && (
          <>
            <Form.Item
              name={[name, [`${row['field-type']}-answer`]]}
              key={[name, [`${row['field-type']}-answer`]]}
              className="forms-field"
              rules={[{ required: isRequired, validator: checkValidCallback }]}
            >
              <Checkbox.Group
                options={checkboxOptions}
              />
            </Form.Item>
          </>
        );

      case 'radio':
        const radioOptions = prepearGroup(row, 'radio')
        return radioOptions?.length > 0 && (
          <>
            <Form.Item
              name={[name, [`${row['field-type']}-answer`]]}
              key={[name, [`${row['field-type']}-answer`]]}
              className="forms-field"
              rules={[{ required: isRequired, validator: checkValidCallback }]}
            >
              <Radio.Group
                options={radioOptions}
              />
            </Form.Item>
          </>
        );

      default: <></>;
    }
  }, [answers, answersRef?.current, formRequired, formRequiredRef?.current])

  // Update parent states based on changes inside Form
  const handleValuesChange = useCallback((changedValues, allValues) => {
    const changedVals = Object.values(allValues);
    const hasAnyAnswer = changedVals?.some(val => val?.['checkbox-answer']?.length > 0 || val?.['radio-answer']?.length > 0);
    if ((!!hasAnyAnswer && !isCancelAll) || !!isCancelAll) {
      setFormRequired(false)
    } else {
      setFormRequired(true)
    }
  }, [])

  const onSubmitHandle = useCallback((data) => {
    setAnswers(data);
    !!onSubmit && !formRequiredRef?.current && onSubmit(data, formName);
  }, [isCancelAll, formRequired, formRequiredRef?.current, formName]);

  const onFinishFailed = ({ values, errorFields, outOfDate }) => {
   // console.error('Failed FBAppointmentAnswer:', { values, errorFields, outOfDate });
    return !formRequiredRef?.current;
  };

  const onSwitchCancelAll = checked => {
    mainForm.resetFields();
    setAnswers({});
    setIsCancelAll(checked);
    setFormRequired(!checked);
  };

  const RenderComponent = ({children}) => !!showWrapper ? (
    <>
      <FBMobileWrapper
        key="appointments-wrapper"
        className={childClassNames}
        showHeader={showHeader}
        showAdminAnswer={showAdminAnswer}
        adminAnswer={adminAnswer}
        types={["appointments"]}
        loading={loading}
      >
        <ErrorBoundary>
          {children}
        </ErrorBoundary>
      </FBMobileWrapper>
    </>
  ) : (
    <ErrorBoundary>
      {children}
    </ErrorBoundary>
  );

  const renderHeader = useMemo(() => {
    return showHeader && (
      <div className='form-header'>
        {!!appointments[`${type}-title`] && (
          <h3 className="form-title">{appointments[`${type}-title`]}</h3>
        )}
        {!!appointments[`${type}-description`] && (
          <p className="form-description">{appointments[`${type}-description`]}</p>
        )}
      </div>
    )
  }, [showHeader, type, appointments]);

  const renderBody = useMemo(() => {
    const totalAmount = appointments[`${type}-fields`]?.length + 1;
    return (
      <>
        {appointments[`${type}-fields`]?.map((row, indx) => {
          const titleDate = formatDateTimeValue(row?.[`field-date`], dateFormat);
          return !!row && (
            <div key={`appointment-answ-row-${indx}`} className={`form-row ${!!isCancelAll ? "cancel-all" : ""}`}>
              <h4 className={`form-row__title appointment ${row[`field-type`]}`}>
                <CustomIcons className="date-icon" type="calendar" />
                {titleDate}
              </h4>
              <div className='form-row__field'>
                <HiddenField
                  key={[indx, `field-type`]}
                  data={{ name: [indx, `field-type`] }}
                  index={indx}
                  value={row['field-type']}
                  required={formRequiredRef?.current}
                />
                <HiddenField
                  key={[indx, `field-date`]}
                  data={{ name: [indx, `field-date`] }}
                  index={indx}
                  value={row?.['field-date']}
                  required={formRequiredRef?.current}
                />
                {generatePreviewByType(row, indx, formRequiredRef?.current)}
              </div>
            </div>
          );
        })}
        {!!isCancelAll && (
          <>
            <HiddenField
              key={[totalAmount, `field-type`]}
              data={{ name: [totalAmount, `field-type`] }}
              index={totalAmount}
              value={"switchCancelAll"}
            />
            <HiddenField
              key={[totalAmount, `field-date`]}
              data={{ name: [totalAmount, `field-date`] }}
              index={totalAmount}
              value={null}
            />
            <HiddenField
              key={[totalAmount, `switchCancelAll-answer`]}
              data={{ name: [totalAmount, `switchCancelAll-answer`] }}
              index={totalAmount}
              value={true}
            />
          </>
        )}
        <SwitchOnCancelAll
          disabled={!!actionRequired && !!values}
          checked={isCancelAll}
          name={[totalAmount, [`switchCancelAll`]]}
          onChange={onSwitchCancelAll} />
      </>
    )
  }, [appointments, type, dateFormat, isCancelAll, actionRequired, values, hideActions, formRequired, formRequiredRef?.current, i18n?.language]);

  const renderBottomActions = useMemo(() => {
    return (!!!hideActions && !!actionRequired && !values) && (
      <div className="actions-wrapper">
        <Button className="btn-previous" onClick={() => {
          mainForm.resetFields();
          setAnswers({});
        }}>
          {t("common.cancel")}
        </Button>
        <Button
          className="confirm-btn"
          form={formName}
          htmlType="submit"
        >
          {t("common.send")}
        </Button>
      </div>
    )
  }, [hideActions, actionRequired, values, formName, i18n?.language]);

  const validateMessages = {
    required: t("inputs.mandatoryField"),
  };

  return !!appointments && (
    <RenderComponent>
      <Form
        name={formName}
        form={mainForm}
        validateMessages={validateMessages}
        className="tcf-fb-forms-answer"
        autoComplete="off"
        onValuesChange={handleValuesChange}
        onFinish={onSubmitHandle}
        onFinishFailed={onFinishFailed}
        disabled={loading || !!values || !actionRequired}
        scrollToFirstError
      >
        {renderHeader}
        {renderBody}
        {renderBottomActions}
      </Form>
    </RenderComponent>
  );
}

FBAppointmentAnswer.defaultProps = {
  type: 'appointments',
  form: null,
  actionRequired: true,
  showHeader: true,
  showWrapper: true,
  showAdminAnswer: false,
  hideActions: false,
};

FBAppointmentAnswer.propTypes = {
  appointments: PropTypes.oneOfType([PropTypes.object, PropTypes.bool, PropTypes.array]),
  values: PropTypes.oneOfType([PropTypes.object, PropTypes.bool, PropTypes.array]),
  onSubmit: PropTypes.func
};

export default memo(FBAppointmentAnswer);
